/**
 * 运行过程数据处理引擎。
 *
 * 这里集中处理第四步执行阶段最容易出错的两类问题：
 * - 同名过程节点：如“修改稿件”在树上出现多次，不能再按 name 全量更新。
 * - 人员工作态：根据 organization 日志把“谁正在执行哪个过程”落到对应角色卡片。
 */

import type { AssignedSlot, ProcessRuntimeState } from '../types';
import type { ProcessGraphCompileResult, ProcessNodeData } from './processGraph';

export type ProcessResolveIntent =
  | 'initialize'
  | 'structure'
  | 'assignRoles'
  | 'runStart'
  | 'runComplete'
  | 'destroy'
  | 'result'
  | 'workerStart'
  | 'workerEnd';

const PROCESS_STATUS_RANK: Record<ProcessRuntimeState['status'], number> = {
  pending: 0,
  initialized: 1,
  structured: 2,
  running: 3,
  completed: 4,
  destroyed: 5,
};

interface ResolveOptions {
  /** 组织日志中的人员姓名，用于精确定位正在工作的角色槽位 */
  personName?: string;
}

/**
 * 给过程节点补齐默认运行态。
 *
 * 叶子节点会按 agents 生成角色槽位；非叶子节点保持空 slots。
 */
export function createProcessRuntimeState(
  node?: ProcessNodeData,
  existing?: ProcessRuntimeState
): ProcessRuntimeState {
  if (existing) {
    return {
      ...existing,
      slots: ensureSlots(node, existing.slots),
    };
  }

  return {
    status: 'pending',
    slots: ensureSlots(node, []),
  };
}

/** 按节点 agents 补齐槽位，保留已匹配人员和工作态。 */
export function ensureSlots(
  node: ProcessNodeData | undefined,
  slots: AssignedSlot[]
): AssignedSlot[] {
  const next = [...slots];
  const roles = node?.agents || [];
  while (next.length < roles.length) {
    next.push({ role: roles[next.length] });
  }
  return next;
}

/** 完成/销毁过程时兜底清理该节点上所有人员的工作态。 */
export function clearSlotWorkingState(slots: AssignedSlot[]): AssignedSlot[] {
  return slots.map(slot =>
    slot.isWorking ? { ...slot, isWorking: false } : slot
  );
}

/** 根据人员姓名切换对应槽位的工作态。 */
export function toggleSlotWorkingState(
  slots: AssignedSlot[],
  personName: string,
  isWorking: boolean
): AssignedSlot[] {
  return slots.map(slot => {
    if (!slot.person || slot.person.name !== personName) return slot;
    if (!!slot.isWorking === isWorking) return slot;
    return { ...slot, isWorking };
  });
}

/**
 * 过程节点解析器：把一条 name/processName 事件定位到唯一过程节点 id。
 *
 * 核心策略：
 * - 初始化/组队阶段优先选择仍未处理的同名节点，保证重复日志逐个落位。
 * - 运行阶段优先选择已 running 的节点；若还没 running，则选择父链路正在执行的节点。
 * - 人员开始/结束运行时，再结合已匹配到该节点的人员槽位做二次筛选。
 */
export class ScenarioRuntimeEngine {
  private readonly graph: ProcessGraphCompileResult;

  private readonly lastResolvedByName = new Map<string, string>();

  constructor(graph: ProcessGraphCompileResult) {
    this.graph = graph;
  }

  resolveProcessId(
    processName: string,
    intent: ProcessResolveIntent,
    processStates: Map<string, ProcessRuntimeState>,
    options: ResolveOptions = {}
  ): string | null {
    const ids = this.graph.nameToProcessIds.get(processName) || [];
    if (ids.length === 0) return null;
    if (ids.length === 1) {
      this.lastResolvedByName.set(processName, ids[0]);
      return ids[0];
    }

    const lastId = this.lastResolvedByName.get(processName);
    const scored = ids
      .map((id, index) => ({
        id,
        index,
        score: this.scoreProcessId(
          id,
          intent,
          processStates,
          lastId,
          options
        ),
      }))
      .sort((a, b) => a.score - b.score || a.index - b.index);

    const target = scored[0]?.id || null;
    if (target) {
      this.lastResolvedByName.set(processName, target);
    }
    return target;
  }

  private scoreProcessId(
    processId: string,
    intent: ProcessResolveIntent,
    processStates: Map<string, ProcessRuntimeState>,
    lastId: string | undefined,
    options: ResolveOptions
  ): number {
    const node = this.graph.processNodeMap.get(processId);
    const state = createProcessRuntimeState(node, processStates.get(processId));
    const statusRank = PROCESS_STATUS_RANK[state.status];
    const isLastResolved = lastId === processId;
    const personAssigned = this.hasAssignedPerson(state, options.personName);
    const personWorking = this.hasWorkingPerson(state, options.personName);
    const runningAncestorDistance = this.getRunningAncestorDistance(
      processId,
      processStates
    );

    if (intent === 'initialize') {
      return state.status === 'pending' ? 0 : 20 + statusRank;
    }

    if (intent === 'structure') {
      if (state.status === 'pending' || state.status === 'initialized') return 0;
      return 20 + statusRank;
    }

    if (intent === 'assignRoles') {
      if (!node?.isLeaf) return 200;
      const filledCount = state.slots.filter(slot => !!slot.person).length;
      const hasEmptySlot = state.slots.some(slot => !slot.person);
      return hasEmptySlot ? filledCount : 80 + filledCount;
    }

    if (intent === 'runStart' || intent === 'workerStart') {
      if (personWorking) return 0;
      if (state.status === 'running' && personAssigned) return 1;
      if (state.status === 'running') return 2;
      if (personAssigned && runningAncestorDistance < Infinity) {
        return 10 + runningAncestorDistance;
      }
      if (runningAncestorDistance < Infinity) {
        return 20 + runningAncestorDistance;
      }
      if (personAssigned) return 30;
      if (
        state.status === 'pending' ||
        state.status === 'initialized' ||
        state.status === 'structured'
      ) {
        return 40 + statusRank;
      }
      return isLastResolved ? 70 : 100 + statusRank;
    }

    if (intent === 'runComplete' || intent === 'workerEnd') {
      if (personWorking) return 0;
      if (state.status === 'running' && personAssigned) return 1;
      if (state.status === 'running') return 2;
      if (personAssigned && isLastResolved) return 3;
      if (isLastResolved) return 4;
      if (personAssigned) return 5;
      return state.status === 'completed' || state.status === 'destroyed'
        ? 90 + statusRank
        : 20 + statusRank;
    }

    if (intent === 'result') {
      if (personWorking) return 0;
      if (state.status === 'running') return 1;
      if (personAssigned && isLastResolved) return 2;
      if (isLastResolved) return 3;
      if (personAssigned) return 4;
      return state.result ? 20 : 40 + statusRank;
    }

    if (intent === 'destroy') {
      if (state.status === 'destroyed') return 100;
      if (state.status === 'completed') return 0;
      return isLastResolved ? 1 : 20 + (5 - statusRank);
    }

    return 100;
  }

  private hasAssignedPerson(
    state: ProcessRuntimeState,
    personName?: string
  ): boolean {
    if (!personName) return false;
    return state.slots.some(slot => slot.person?.name === personName);
  }

  private hasWorkingPerson(
    state: ProcessRuntimeState,
    personName?: string
  ): boolean {
    if (!personName) return false;
    return state.slots.some(
      slot => slot.person?.name === personName && slot.isWorking
    );
  }

  private getRunningAncestorDistance(
    processId: string,
    processStates: Map<string, ProcessRuntimeState>
  ): number {
    let distance = 1;
    let parentId = this.graph.processNodeMap.get(processId)?.parentProcessId;
    while (parentId) {
      const parentState = processStates.get(parentId);
      if (parentState?.status === 'running') return distance;
      parentId = this.graph.processNodeMap.get(parentId)?.parentProcessId;
      distance += 1;
    }
    return Infinity;
  }
}
